import React, { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { acquireActivitySocket } from "../lib/activitySocketPool";
import ChatPanel from "./ChatPanel";
import { useIsMobile } from "../lib/useIsMobile";
import { colors, fontMono, formatUtcTime, formatFrequencyDisplay, s } from "../lib/theme";

export default function QsoLog() {
  const { activityId } = useParams();
  const [params] = useSearchParams();
  const { session } = useAuth();
  const band = params.get("band");
  const mode = params.get("mode");
  const presetFreq = params.get("freq");
  // Normalmente la frecuencia viene fija de la celda reclamada — pero si
  // llega vacía (caso del organizador registrando a mano en una actividad
  // ya finalizada, sin ninguna celda de la que tomarla), se puede escribir.
  const [freq, setFreq] = useState(presetFreq || "");

  const [callsign, setCallsign] = useState("");
  const [lookupName, setLookupName] = useState(null);
  const [lookupSource, setLookupSource] = useState(null);
  const [activity, setActivity] = useState(null);
  const [chatData, setChatData] = useState(null);
  const chatSocketRef = useRef(null);
  const VOICE_MODES = ["SSB", "FM", "AM"];
  const [rstSent, setRstSent] = useState(VOICE_MODES.includes(mode) ? "59" : "599");
  const [rstReceived, setRstReceived] = useState("");
  const [exchange, setExchange] = useState("");
  const [exchangeAuto, setExchangeAuto] = useState(true); // sigue el número automático mientras no se toque a mano
  const [locator, setLocator] = useState("");
  const [sigInfo, setSigInfo] = useState("");
  const [qsos, setQsos] = useState([]);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const isMobile = useIsMobile();
  const [chatOpen, setChatOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({});
  const inputRef = useRef(null);

  useEffect(() => {
    api.getActivity(activityId).then(setActivity).catch(() => {});
  }, [activityId]);

  // Comparte la misma conexión que Matriz para esta actividad (con margen
  // de gracia al salir) — así, navegar entre Matriz y Log no se anuncia
  // como una desconexión/reconexión real en el chat.
  useEffect(() => {
    const { socket, release } = acquireActivitySocket(activityId, session.token, (res) => {
      if (!res?.error) setChatData(res.chat || null);
    });
    chatSocketRef.current = socket;
    return release;
  }, [activityId, session.token]);

  const refreshExchangePreview = (forceAuto = false) => {
    api.nextExchange(activityId, band, mode).then((data) => {
      if (data.applies && (exchangeAuto || forceAuto)) {
        setExchange(String(data.nextNumber).padStart(3, "0"));
      }
    }).catch(() => {});
  };

  useEffect(() => {
    refreshExchangePreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId, band, mode]);

  const loadQsos = (scope) => {
    api.listQsos(activityId, scope).then(setQsos).catch((err) => setError(err.message));
  };

  useEffect(() => {
    loadQsos(showAll ? "all" : "mine");
    inputRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAll]);

  useEffect(() => {
    setLookupName(null);
    setLookupSource(null);
    if (callsign.trim().length < 3) return;
    const t = setTimeout(() => {
      api.lookupCallsign(activityId, callsign.trim()).then((result) => {
        if (result?.name) {
          setLookupName(result.name);
          setLookupSource(result.source);
        }
        if (result?.grid && activity?.require_locator) setLocator(result.grid);
      }).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [callsign, activityId, activity?.require_locator]);

  const save = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await api.createQso(activityId, {
        callsign, band, mode, frequency: freq,
        rstSent, rstReceived, exchange, locator, sigInfo,
      });
      setCallsign(""); setRstReceived("");
      setExchangeAuto(true);
      loadQsos(showAll ? "all" : "mine");
      refreshExchangePreview(true);
      inputRef.current?.focus();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (qsoId) => {
    await api.deleteQso(activityId, qsoId);
    loadQsos(showAll ? "all" : "mine");
  };

  const startEdit = (q) => {
    setEditingId(q.id);
    setEditDraft({
      callsign: q.callsign,
      rstSent: q.rst_sent || "",
      rstReceived: q.rst_received || "",
      exchange: q.exchange || "",
      locator: q.locator || "",
      sigInfo: q.sig_info || "",
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const saveEdit = async (qsoId) => {
    try {
      await api.updateQso(activityId, qsoId, editDraft);
      setEditingId(null);
      loadQsos(showAll ? "all" : "mine");
    } catch (err) {
      setError(err.message);
    }
  };

  if (!band || !mode || !freq) {
    return (
      <div style={s.page}>
        <div style={s.errorBox}>
          Falta el contexto de banda/modo/frecuencia. Vuelve a la matriz y entra al Log desde una celda reclamada.
        </div>
        <Link to={`/actividades/${activityId}/matriz`} style={{ color: colors.amber }}>← Volver a la matriz</Link>
      </div>
    );
  }

  return (
    <div style={{ ...s.page, maxWidth: 1500 }}>
      <Link to={`/actividades/${activityId}/matriz`} style={{ display: "inline-flex", gap: 6, color: colors.textDim, fontWeight: 700, fontSize: 14, textDecoration: "none", marginBottom: 16 }}>
        ← Volver a la matriz
      </Link>

      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
      <div style={{ flex: 1, minWidth: 0 }}>

      <div style={{ ...s.card, marginBottom: 10, display: "flex", gap: 8, alignItems: "center" }}>
        <Chip label={band} />
        <Chip label={mode} />
        {presetFreq ? (
          <Chip label={`${formatFrequencyDisplay(freq)} MHz`} accent />
        ) : (
          <input
            value={freq}
            onChange={(e) => setFreq(e.target.value)}
            placeholder="Frecuencia (MHz)"
            style={{ ...s.input, width: 140, fontSize: 13 }}
          />
        )}
      </div>
      {!presetFreq && (
        <p style={{ fontSize: 11.5, color: colors.textFaint, marginTop: -4, marginBottom: 10 }}>
          Actividad finalizada — registro a mano, sin celda reclamada. Escribe la frecuencia del contacto.
        </p>
      )}

      <form onSubmit={save} style={{ ...s.card, display: "flex", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ flex: "1 1 200px" }}>
          <label style={s.label}>INDICATIVO</label>
          <input
            ref={inputRef}
            value={callsign}
            onChange={(e) => setCallsign(e.target.value.toUpperCase())}
            placeholder="EA5NEW"
            style={{ ...s.input, fontSize: 18, fontWeight: 600 }}
            autoComplete="off"
          />
          {/* Altura siempre reservada (esté visible el nombre o no), para que
              ni empuje el resto del formulario al aparecer, ni se monte
              encima del contenido de más abajo cuando aparece. */}
          <div style={{ minHeight: 22, marginTop: 5 }}>
            {lookupName && (
              <span style={{ fontSize: 12.5, color: colors.amber }}>
                👤 {lookupName} <span style={{ color: colors.textFaint }}>({lookupSource === "qrz" ? "QRZ" : "HamQTH"})</span>
              </span>
            )}
          </div>
        </div>
        {(activity?.require_rst ?? true) && (
          <>
            <MiniField label="RST ENV." value={rstSent} onChange={setRstSent} width={70} />
            <MiniField label="RST REC." value={rstReceived} onChange={setRstReceived} width={70} />
          </>
        )}
        {activity?.require_exchange && (
          <MiniField label="EXCHANGE" value={exchange} onChange={(v) => { setExchange(v); setExchangeAuto(false); }} width={100} />
        )}
        {activity?.require_locator && (
          <MiniField label="LOCATOR/QTH" value={locator} onChange={setLocator} width={110} />
        )}
        {activity?.is_ota && (
          <MiniField label="SIG_INFO" value={sigInfo} onChange={(v) => setSigInfo(v.slice(0, 30))} width={140} />
        )}
        <div>
          <label style={{ ...s.label, visibility: "hidden" }}>·</label>
          <button type="submit" disabled={!callsign} style={{ ...s.btnPrimary, height: 40, opacity: !callsign ? 0.5 : 1 }}>
            ✓ Guardar QSO
          </button>
        </div>
      </form>

      {error && <div style={s.errorBox}>{error}</div>}

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <div style={s.eyebrow}>REGISTRO — {qsos.length} QSO</div>
        <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
          <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 12.5, color: colors.textDim, cursor: "pointer" }}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Ver todo el log de la actividad
          </label>
          <button
            onClick={() => api.downloadExport(activityId, "adif", "mine", "mi_log").catch((err) => setError(err.message))}
            style={{ ...exportLinkStyle, background: "transparent", border: "none", cursor: "pointer" }}
          >
            Exportar mi log (ADIF)
          </button>
        </div>
      </div>

      <div style={{ border: `1px solid ${colors.line}`, borderRadius: 10, overflow: "auto", maxHeight: 460 }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <Th>Hora</Th><Th>Indicativo</Th>
              {(activity?.require_rst ?? true) && <Th>RST E/R</Th>}
              {activity?.require_exchange && <Th>Exchange</Th>}
              {activity?.require_locator && <Th>Locator</Th>}
              {activity?.is_ota && <Th>SIG_INFO</Th>}
              {showAll && <Th>Operador</Th>}<Th></Th>
            </tr>
          </thead>
          <tbody>
            {qsos.map((q) => {
              const isEditing = editingId === q.id;
              return (
                <tr key={q.id}>
                  <Td>{formatUtcTime(q.logged_at)}</Td>
                  <Td style={{ fontWeight: 700 }}>
                    {isEditing ? (
                      <input
                        value={editDraft.callsign}
                        onChange={(e) => setEditDraft({ ...editDraft, callsign: e.target.value.toUpperCase() })}
                        style={{ ...s.input, width: 110, fontSize: 13 }}
                      />
                    ) : q.callsign}
                  </Td>
                  {(activity?.require_rst ?? true) && (
                    <Td>
                      {isEditing ? (
                        <div style={{ display: "flex", gap: 4 }}>
                          <input value={editDraft.rstSent} onChange={(e) => setEditDraft({ ...editDraft, rstSent: e.target.value })} style={{ ...s.input, width: 55, fontSize: 13 }} />
                          <input value={editDraft.rstReceived} onChange={(e) => setEditDraft({ ...editDraft, rstReceived: e.target.value })} style={{ ...s.input, width: 55, fontSize: 13 }} />
                        </div>
                      ) : `${q.rst_sent || ""}${q.rst_received ? ` / ${q.rst_received}` : ""}`}
                    </Td>
                  )}
                  {activity?.require_exchange && (
                    <Td>
                      {isEditing ? (
                        <input value={editDraft.exchange} onChange={(e) => setEditDraft({ ...editDraft, exchange: e.target.value })} style={{ ...s.input, width: 70, fontSize: 13 }} />
                      ) : q.exchange}
                    </Td>
                  )}
                  {activity?.require_locator && (
                    <Td>
                      {isEditing ? (
                        <input value={editDraft.locator} onChange={(e) => setEditDraft({ ...editDraft, locator: e.target.value.toUpperCase() })} style={{ ...s.input, width: 70, fontSize: 13 }} />
                      ) : q.locator}
                    </Td>
                  )}
                  {activity?.is_ota && (
                    <Td>
                      {isEditing ? (
                        <input value={editDraft.sigInfo} onChange={(e) => setEditDraft({ ...editDraft, sigInfo: e.target.value.slice(0, 30) })} style={{ ...s.input, width: 110, fontSize: 13 }} />
                      ) : q.sig_info}
                    </Td>
                  )}
                  {showAll && <Td>{q.operator_callsign}</Td>}
                  <Td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    {isEditing ? (
                      <>
                        <button onClick={() => saveEdit(q.id)} style={saveBtnStyle}>Guardar</button>{" "}
                        <button onClick={cancelEdit} style={cancelBtnStyle}>Cancelar</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startEdit(q)} style={editBtnStyle}>Editar</button>{" "}
                        <button
                          onClick={() => {
                            if (window.confirm(`¿Borrar el QSO con ${q.callsign}? No se puede deshacer.`)) remove(q.id);
                          }}
                          style={deleteBtnStyle}
                        >
                          Borrar
                        </button>
                      </>
                    )}
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      </div>

      {chatData && !isMobile && (
        <div style={{ position: "sticky", top: 20 }}>
          <ChatPanel
            socket={chatSocketRef.current}
            activityId={activityId}
            initialChat={chatData}
            myUserId={session.user.id}
          />
        </div>
      )}
      </div>

      {/* Móvil: mismo criterio que en la Matriz — el chat no cabe al lado
          de la tabla, se abre como panel flotante en vez de solaparse. */}
      {chatData && isMobile && (
        <>
          <button
            onClick={() => setChatOpen(true)}
            style={{
              position: "fixed", right: 16, bottom: 16, zIndex: 40,
              background: colors.amber, color: "#1a1200", border: "none", borderRadius: 999,
              padding: "12px 18px", fontSize: 14, fontWeight: 700, cursor: "pointer",
              boxShadow: "0 4px 14px rgba(0,0,0,0.4)",
            }}
          >
            💬 Chat
          </button>
          {chatOpen && (
            <div style={{
              position: "fixed", inset: 0, zIndex: 50, background: "rgba(0,0,0,0.6)",
              display: "flex", flexDirection: "column", justifyContent: "flex-end",
            }} onClick={() => setChatOpen(false)}>
              <div style={{ maxHeight: "80vh" }} onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 6px 0" }}>
                  <button
                    onClick={() => setChatOpen(false)}
                    style={{ background: colors.panelRaised, border: `1px solid ${colors.lineStrong}`, color: colors.text, borderRadius: 6, padding: "6px 12px", fontSize: 13, cursor: "pointer" }}
                  >
                    Cerrar ✕
                  </button>
                </div>
                <ChatPanel
                  socket={chatSocketRef.current}
                  activityId={activityId}
                  initialChat={chatData}
                  myUserId={session.user.id}
                  containerStyle={{ width: "100%", minWidth: 0, height: "70vh", borderRadius: "12px 12px 0 0" }}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Chip({ label, accent }) {
  return (
    <span style={{
      fontFamily: fontMono, fontSize: 12.5, fontWeight: 700, border: `1.5px solid ${accent ? colors.amber : colors.lineStrong}`,
      color: accent ? colors.amber : colors.text, borderRadius: 5, padding: "4px 10px",
    }}>{label}</span>
  );
}
function MiniField({ label, value, onChange, width }) {
  return (
    <div style={{ width }}>
      <label style={s.label}>{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)} style={s.input} autoComplete="off" />
    </div>
  );
}
function Th({ children }) {
  return <th style={{ position: "sticky", top: 0, zIndex: 1, textAlign: "left", fontSize: 11, color: colors.textFaint, fontWeight: 700, padding: "9px 12px", background: colors.panelRaised, borderBottom: `1px solid ${colors.lineStrong}` }}>{children}</th>;
}
function Td({ children, style }) {
  return <td style={{ fontFamily: fontMono, fontSize: 13, padding: "8px 12px", borderBottom: "1px solid #16202a", ...style }}>{children}</td>;
}
const exportLinkStyle = { fontSize: 12, fontWeight: 700, color: colors.amber, textDecoration: "none" };
const deleteBtnStyle = { background: "transparent", border: `1px solid ${colors.redBorder}`, color: "#ffb4b4", borderRadius: 5, fontSize: 11, fontWeight: 700, padding: "4px 9px", cursor: "pointer" };
const editBtnStyle = { background: "transparent", border: `1px solid ${colors.lineStrong}`, color: colors.textDim, borderRadius: 5, fontSize: 11, fontWeight: 700, padding: "4px 9px", cursor: "pointer" };
const saveBtnStyle = { background: colors.amber, border: "none", color: "#1a1200", borderRadius: 5, fontSize: 11, fontWeight: 700, padding: "4px 9px", cursor: "pointer" };
const cancelBtnStyle = { background: "transparent", border: `1px solid ${colors.lineStrong}`, color: colors.textFaint, borderRadius: 5, fontSize: 11, fontWeight: 700, padding: "4px 9px", cursor: "pointer" };
